import React, { useEffect, useId, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const ACCEPTED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".pdf"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
const ACCEPTED_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
]);

function getExtension(fileName = "") {
  const match = String(fileName).toLowerCase().match(/(\.[a-z0-9]+)$/);
  return match ? match[1] : "";
}

function isAllowedFile(file) {
  if (!file) return false;
  const extension = getExtension(file.name);
  if (ACCEPTED_EXTENSIONS.includes(extension)) return true;
  return Boolean(file.type && ACCEPTED_MIME_TYPES.has(file.type));
}

function getFileNameFromUrl(url) {
  if (!url) return "";
  try {
    const pathname = new URL(url).pathname;
    const rawName = decodeURIComponent(pathname.split("/").pop() || "");
    return rawName.split("?")[0] || "Documento anexado";
  } catch {
    const fallback = String(url).split("/").pop() || "";
    return decodeURIComponent(fallback.split("?")[0] || "") || "Documento anexado";
  }
}

function DocumentField({ url, disabled, onUpload, onClear, error, errorId }) {
  const inputRef = useRef(null);
  const generatedId = useId();
  const inputId = `vehicle-document-input-${generatedId}`;
  const [fileName, setFileName] = useState("");
  const [typeError, setTypeError] = useState("");

  useEffect(() => {
    if (!url) {
      setFileName("");
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [url]);

  const hasFile = Boolean(url || fileName);
  const displayName = fileName || (url ? getFileNameFromUrl(url) : "");
  const showError = Boolean(error || typeError);
  const describedBy = [error ? errorId : null, typeError ? `${inputId}-type-error` : null]
    .filter(Boolean)
    .join(" ") || undefined;

  const openFilePicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedFile(file)) {
      setTypeError("Formato inválido. Use PNG, JPG, JPEG ou PDF.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setTypeError("");
    setFileName(file.name);
    onUpload?.(file);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    if (disabled) return;
    setFileName("");
    setTypeError("");
    if (inputRef.current) inputRef.current.value = "";
    onClear?.();
  };

  return (
    <Box
      className={
        showError
          ? "new-vehicle-document-field has-error"
          : "new-vehicle-document-field"
      }
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT_ATTR}
        disabled={disabled}
        className="new-vehicle-document-input"
        aria-invalid={showError ? "true" : undefined}
        aria-describedby={describedBy}
        onChange={handleFileChange}
      />

      {hasFile ? (
        <Box
          className={`new-vehicle-document-control is-selected${disabled ? " is-disabled" : ""}`}
          role="group"
          aria-label="Documento selecionado"
        >
          <AttachFileOutlinedIcon
            className="new-vehicle-document-icon"
            fontSize="small"
          />
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="new-vehicle-document-name"
              title={displayName}
              onClick={(event) => {
                // Garante abertura da downloadURL persistida, sem passar pelo Storage SDK.
                event.preventDefault();
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              {displayName}
            </a>
          ) : (
            <span className="new-vehicle-document-name" title={displayName}>
              {displayName}
            </span>
          )}
          {!disabled && (
            <IconButton
              size="small"
              className="new-vehicle-document-clear"
              onClick={handleClear}
              aria-label="Remover arquivo"
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box
          component="button"
          type="button"
          className={`new-vehicle-document-control${disabled ? " is-disabled" : ""}`}
          onClick={openFilePicker}
          disabled={disabled}
          aria-invalid={showError ? "true" : undefined}
          aria-describedby={describedBy}
        >
          <CloudUploadOutlinedIcon
            className="new-vehicle-document-icon"
            fontSize="small"
          />
          <Box className="new-vehicle-document-copy">
            <Typography component="span" className="new-vehicle-document-title">
              Selecionar arquivo
            </Typography>
            <Typography component="span" className="new-vehicle-document-hint">
              PNG, JPG, JPEG ou PDF
            </Typography>
          </Box>
        </Box>
      )}

      {typeError ? (
        <span
          id={`${inputId}-type-error`}
          className="new-vehicle-field-error"
          role="alert"
        >
          {typeError}
        </span>
      ) : null}
    </Box>
  );
}

export default DocumentField;
